<template>
	<span />
</template>

<script>
import { mapGetters } from 'vuex';
import { EVENT, bus } from '@videotools/js/events';
import storage from '@shared/js/storage';
import { AdType } from '@shared/enums/AdType';
import { TakeoverPosition } from '@shared/enums/TakeoverPosition';
import { AdRotationMode } from '@shared/enums/AdRotationMode';
import {
	DEFAULT_DURATION_SECONDS,
	getIntervalDisplayTiming,
	getIntervalDisplaySlotIndex,
	isOnceDisplayMode,
} from '@components/player/monetization/displayTiming';
import { getEffectiveCreativeRotationMode } from '@components/player/monetization/creativeRotation';

const SEEK_POSITION_DELTA_SECONDS = 2.5;

export default {
	name:  'MonetizationTakeoverRenderer',
	props: {
		playerIsAdMode: {
			type:    Boolean,
			default: false,
		},
		position: {
			type: Number,
		},
		event: {
			type:    Object,
			default: null,
		},
		ads: {
			type:    Array,
			default: () => [],
		},
		isCouch: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			timeout:               null,
			activeImageLoadKey:    null,
			imageLoaded:           false,
			imageLoadRequestId:    0,
			loadStartedAtPosition: null,
			imageLoadedAtPosition: null,
			onceDisplayRequested:  false,
			positionUpdated:       false,
			randomizedCreativeIds: [],
			randomizedPass:        null,
			emittedStateKey:       null,
		};
	},
	computed: {
		...mapGetters({
			isTaggingToolMode: 'app/isTaggingToolMode',
		}),
		ad() {
			return this.ads?.find(ad => ad.type === AdType.TAKEOVER_AD);
		},
		creatives() {
			const creatives = this.ad?.creatives?.length
				? this.ad.creatives
				: [];

			return [...creatives].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
		},
		currentCreative() {
			if (!this.creatives.length) {
				return null;
			}
			if (this.effectiveRotationMode === AdRotationMode.RANDOMIZED && this.randomizedCreativeIds.length) {
				const creativeId = this.randomizedCreativeIds[this.displaySlotIndex % this.randomizedCreativeIds.length];
				return this.creatives.find(creative => creative.id === creativeId) || this.creatives[0];
			}
			return this.creatives[this.displaySlotIndex % this.creatives.length];
		},
		effectiveRotationMode() {
			return getEffectiveCreativeRotationMode(this.displayMode, this.ad?.rotationMode, this.creatives.length);
		},
		displayMode() {
			return this.ad?.displayMode;
		},
		displayIntervalSeconds() {
			return this.ad?.displayIntervalSeconds;
		},
		displayDurationSeconds() {
			return this.ad?.displayDurationSeconds;
		},
		displaySlotIndex() {
			return getIntervalDisplaySlotIndex(this.displayMode, this.position, this.displayIntervalSeconds, this.displayDurationSeconds);
		},
		displayTiming() {
			return getIntervalDisplayTiming(this.displayMode, this.displayIntervalSeconds, this.displayDurationSeconds);
		},
		displaySlotPosition() {
			if (!this.displayTiming) {
				return 0;
			}

			return (Number(this.position) || 0) % this.displayTiming.intervalSeconds;
		},
		canDisplayAd() {
			return !(
				this.playerIsAdMode ||
				this.isCouch ||
				this.isTaggingToolMode ||
				!this.ad ||
				!this.currentCreative ||
				!this.positionUpdated
			);
		},
		loadDelaySeconds() {
			if (!Number.isFinite(this.loadStartedAtPosition) || !Number.isFinite(this.imageLoadedAtPosition)) {
				return 0;
			}

			return Math.max(0, this.imageLoadedAtPosition - this.loadStartedAtPosition);
		},
		imageLoadKey() {
			if (!this.canDisplayAd || !this.currentCreative?.imageUrl) {
				return null;
			}

			if (this.displayTiming && this.displaySlotPosition < this.displayTiming.durationSeconds) {
				return [
					'interval',
					this.displaySlotIndex,
					this.currentCreative.id,
					this.currentCreative.imageUrl,
				].join('|');
			}

			if (isOnceDisplayMode(this.displayMode) && this.onceDisplayRequested) {
				return [
					'once',
					this.currentCreative.id,
					this.currentCreative.imageUrl,
				].join('|');
			}

			return null;
		},
		adVisible() {
			if (!this.canDisplayAd) {
				return false;
			}

			if (this.displayTiming) {
				return this.displaySlotPosition < this.displayTiming.durationSeconds + this.loadDelaySeconds;
			}

			if (isOnceDisplayMode(this.displayMode)) {
				return Boolean(this.timeout);
			}

			return false;
		},
		hasVast() {
			return this.ads.find(ad => ad.type === AdType.PLAYER_AD);
		},
		state() {
			if (!this.adVisible || !this.imageLoaded) {
				return null;
			}

			return {
				visible:             true,
				imageUrl:            this.currentCreative.imageUrl,
				link:                this.appendAdAttributes(this.currentCreative.link),
				playerScalePercent:  this.ad.playerScalePercent || 80,
				takeoverPosition:    this.ad.takeoverPosition || TakeoverPosition.BOTTOM_LEFT,
				currentCreativeId:   this.currentCreative.id,
				currentCreativeLink: this.currentCreative.link,
			};
		},
	},
	watch: {
		position(value, previousValue) {
			this.positionUpdated = true;
			if (
				this.activeImageLoadKey &&
				!this.imageLoaded &&
				Number.isFinite(value) &&
				Number.isFinite(previousValue) &&
				Math.abs(value - previousValue) > SEEK_POSITION_DELTA_SECONDS
			) {
				this.resetImageLoad();
			}
		},
		ad() {
			this.resetCreativeRotation();
			this.resetImageLoad();
		},
		displaySlotIndex() {
			this.resetImageLoad();
			this.ensureRandomizedOrder();
		},
		canDisplayAd(value) {
			if (!value) {
				this.resetImageLoad();
			}
		},
		imageLoadKey(value) {
			if (value) {
				this.loadImage(value);
			}
		},
		state: {
			immediate: true,
			handler(value) {
				const stateKey = this.getStateKey(value);
				if (stateKey === this.emittedStateKey) {
					return;
				}

				this.emittedStateKey = stateKey;
				this.$emit('state-change', value);
			},
		},
	},
	mounted() {
		this.resetCreativeRotation();

		if (isOnceDisplayMode(this.displayMode)) {
			if (!this.hasVast) {
				this.startTenSecondOnceDisplay();
			} else {
				bus.subscribe(EVENT.PLAYER.ON.MONETIZATION_AD_START, this.startTenSecondOnceDisplay);
			}
		}
	},
	destroyed() {
		this.$emit('state-change', null);
		clearTimeout(this.timeout);
		this.imageLoadRequestId += 1;
		if (this.hasVast && isOnceDisplayMode(this.displayMode)) {
			bus.unsubscribe(EVENT.PLAYER.ON.MONETIZATION_AD_START, this.startTenSecondOnceDisplay);
		}
	},
	methods: {
		getStateKey(state) {
			if (!state) {
				return 'hidden';
			}

			return [
				state.imageUrl,
				state.link,
				state.playerScalePercent,
				state.takeoverPosition,
				state.currentCreativeId,
			].join('|');
		},
		resetCreativeRotation() {
			this.randomizedCreativeIds = [];
			this.randomizedPass = null;
			this.ensureRandomizedOrder();
		},
		ensureRandomizedOrder() {
			if (this.effectiveRotationMode !== AdRotationMode.RANDOMIZED || !this.creatives.length) {
				return;
			}

			const pass = Math.floor(this.displaySlotIndex / this.creatives.length);
			if (this.randomizedPass === pass && this.randomizedCreativeIds.length === this.creatives.length) {
				return;
			}

			const previousLast = this.randomizedCreativeIds[this.randomizedCreativeIds.length - 1];
			const ids = this.creatives.map(creative => creative.id);
			for (let i = ids.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[ids[i], ids[j]] = [ids[j], ids[i]];
			}

			if (ids.length > 1 && ids[0] === previousLast) {
				[ids[0], ids[1]] = [ids[1], ids[0]];
			}

			this.randomizedCreativeIds = ids;
			this.randomizedPass = pass;
		},
		startTenSecondOnceDisplay() {
			clearTimeout(this.timeout);
			this.timeout = null;
			this.onceDisplayRequested = true;
			this.resetImageLoad();
		},
		loadImage(imageLoadKey) {
			if (this.activeImageLoadKey === imageLoadKey) {
				return;
			}

			this.resetImageLoad(false);
			this.activeImageLoadKey = imageLoadKey;
			this.loadStartedAtPosition = Number(this.position) || 0;

			const requestId = ++this.imageLoadRequestId;
			const image = new Image();
			let didFinishLoading = false;
			const finishLoading = () => {
				if (didFinishLoading || requestId !== this.imageLoadRequestId) {
					return;
				}

				didFinishLoading = true;
				this.imageLoaded = true;
				this.imageLoadedAtPosition = Number(this.position) || 0;

				if (isOnceDisplayMode(this.displayMode)) {
					this.startOnceDisplayTimer();
				}
			};

			image.onload = finishLoading;
			image.onerror = finishLoading;
			image.src = this.currentCreative.imageUrl;

			if (image.complete) {
				finishLoading();
			}
		},
		resetImageLoad(invalidateLoading = true) {
			if (invalidateLoading) {
				this.imageLoadRequestId += 1;
			}

			this.activeImageLoadKey = null;
			this.imageLoaded = false;
			this.loadStartedAtPosition = null;
			this.imageLoadedAtPosition = null;
		},
		startOnceDisplayTimer() {
			clearTimeout(this.timeout);
			this.onceDisplayRequested = false;
			this.timeout = setTimeout(() => {
				this.timeout = null;
				this.resetImageLoad();
			}, DEFAULT_DURATION_SECONDS * 1000);
		},
		appendAdAttributes(url) {
			if (!url) {
				return url;
			}

			let imageUrl = this.event ? `${url}/${this.event.id}` : url;

			if (this.event && storage.viewerId) {
				imageUrl = `${imageUrl}/${storage.viewerId}`;
			}

			return imageUrl;
		},
	},
};
</script>
